/**
 * Vuex actions for the "spaces" (channels) module.
 *
 * Fetches and mutates channel/space data: loading a single channel, listing
 * channels for an owner, creating a channel, and reading/paginating the
 * current user's followed-channels list. Each action calls the backend API
 * then commits the result via the mutations in updates.js.
 */
import { showNotification } from '@/plugins/notification';

export default {
  /** Fetch a single channel by id and store it as `state.channelData`. */
  async fetchChannelData({ commit }, id) {
    const response = await fetch(`/api/channels/${id}`);
    let data = await response.json();
    commit('setChannelData', data);
  },

  /** Reset `state.channelData` to an empty object. */
  async clearChannelData({ commit }, id) {
    let data = {};
    commit('setChannelData', data);
  },

  /** Fetch the (max 15) channels owned by the given owner and store the results as `state.channelsList`. */
  async fetchChannelsList({ commit }, payload) {
    let params = new URLSearchParams({
      limit: '15',
      ownerType: payload.ownerType,
      ownerId: payload.ownerId,
    }).toString();

    const response = await fetch(`/api/channels?${params}`);
    let data = await response.json();
    commit('setChannelsList', data.results);
  },

  /** Create a new channel via the API; on success store it as `state.channelData`, on failure show an error notification. */
  async fetchCreateChannel({ commit }, payload) {
    let response = await fetch(`/api/channels`, {
      method: 'POST',
      body: JSON.stringify({
        id: payload.id,
        channelId: payload.channelId,
        name: payload.name,
        description: payload.description,
        avatarFileId: payload.avatarFileId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    let data = await response.json();
    if (data.statusCode) {
      showNotification('error', `Error`, data.message, 7000);
    } else {
      commit('setChannelData', data);
    }
    return response;
  },

  /**
   * Fetch a page of channels the given user is following (optionally filtered
   * by `followType`) and replace `state.followingList`/`state.followingCount`
   * with the results. Guards against non-JSON responses and request errors by
   * resetting the list/count to empty rather than throwing.
   */
  async fetchFollowingList({ commit }, { userId, offset = 0, limit = 15, followType }) {
    try {
      // followType = followType === 'negative' ? 'monitor' : 'follow';
      let params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
      if (followType) params.set('followType', followType);
      const response = await fetch(`/api/users/${userId}/following-channels?${params.toString()}` , { credentials: 'include' });
      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await response.text().catch(() => '');
        console.error('[fetchFollowingList] Non-JSON response', { status: response.status, snippet: text.slice(0, 200) });
        commit('setFollowingList', []);
        commit('setFollowingCount', 0);
        return { results: [], count: 0 };
      }
      const data = await response.json();
      if (response.ok && data && Array.isArray(data.results)) {
        commit('setFollowingList', data.results);
        commit('setFollowingCount', data.count || 0);
        return data;
      } else {
        commit('setFollowingList', []);
        commit('setFollowingCount', 0);
        return { results: [], count: 0 };
      }
    } catch (e) {
      commit('setFollowingList', []);
      commit('setFollowingCount', 0);
      return { results: [], count: 0 };
    }
  },

  /**
   * Fetch the next page of the current user's followed channels and append
   * the new, non-duplicate results onto `state.followingList` (via
   * `addToFollowingList`), for infinite-scroll style pagination.
   */
  async fetchNextFollowingPage({ commit }, { userId, offset, limit = 15, only }) {
    try {
      let params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
      if (only) params.set('only', only);
      const response = await fetch(`/api/users/${userId}/following-channels?${params.toString()}` , { credentials: 'include' });
      const ct = response.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        const text = await response.text().catch(() => '');
        console.error('[fetchNextFollowingPage] Non-JSON response', { status: response.status, snippet: text.slice(0, 200) });
        return { results: [] };
      }
      const data = await response.json();
      if (response.ok && data && Array.isArray(data.results)) {
        commit('addToFollowingList', data.results);
        return data;
      } else {
        return { results: [] };
      }
    } catch (e) {
      return { results: [] };
    }
  },
};
